import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Loader,
  RangeSlider,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { IconCalendar, IconClock } from "@tabler/icons-react";
import { MantineReactTable, type MRT_ColumnDef, type MRT_ColumnFiltersState, type MRT_RowSelectionState } from "mantine-react-table";
import { IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { successNotification } from "@/shared/utils/successNotification";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { AdminStatusBadge, UCC_STATUS_OPTIONS } from "@/pages/admin/AdminStatusBadge";
import { formatDate } from "@/shared/utils/format.utils";
import { enumLabel, GENDER_LABEL, OCCUPATION_LABEL, INCOME_SLAB_LABEL } from "@/shared/constants/general.const";
import ConfirmNotificationModal from "./ConfirmNotificationModal";

type UserRow = {
  id: string;
  name: string;
  email: string;
  age: number;
  investmentAccountStatus: string | null;
  goalsCreated: number;
  dateOfBirth: string;
  gender: string | null;
  panNumber: string | null;
  investorType: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  placeOfBirth: string | null;
  occupation: string | null;
  incomeSlab: string | null;
  referralCode: string | null;
  referralCount: number;
  createdAt: string;
  updatedAt: string;
};

const AGE_MIN = 18;
const AGE_MAX = 100;
const GENDER_OPTIONS = Object.entries(GENDER_LABEL).map(([value, label]) => ({ value, label }));
const OCCUPATION_OPTIONS = Object.entries(OCCUPATION_LABEL).map(([value, label]) => ({ value, label }));
const INCOME_SLAB_OPTIONS = Object.entries(INCOME_SLAB_LABEL).map(([value, label]) => ({ value, label }));

function buildScheduledAt(date: Date, time: string): string {
  const combined = new Date(date);
  if (time) {
    const [hours, minutes] = time.split(":").map(Number);
    combined.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  }
  return combined.toISOString();
}

export default function CreateNotificationPage() {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  // ─── Form state ──────────────────────────────────────────────────────────────
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const [scheduleTime, setScheduleTime] = useState("");

  // ─── Recipient state ─────────────────────────────────────────────────────────
  const [users, setUsers] = useState<UserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [ageRange, setAgeRange] = useState<[number, number]>([AGE_MIN, AGE_MAX]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

  const EXACT_FIELDS = ["gender", "occupation", "incomeSlab", "investmentAccountStatus"];

  const filteredUsers = useMemo(() => {
    let result = users.filter((u) => u.age >= ageRange[0] && u.age <= ageRange[1]);
    for (const f of columnFilters) {
      const val = f.value;
      if (val === undefined || val === null || val === "") continue;
      result = result.filter((u) => {
        const cell = (u as any)[f.id];
        if (Array.isArray(val)) {
          if (f.id === "referralCount") {
            const [min, max] = val;
            if (min !== "" && min != null && cell < Number(min)) return false;
            if (max !== "" && max != null && cell > Number(max)) return false;
            return true;
          }
          const [from, to] = val as [Date | null, Date | null];
          if (!from && !to) return true;
          const date = cell ? new Date(cell) : null;
          if (!date) return true;
          if (from && date < new Date(from)) return false;
          if (to && date > new Date(to)) return false;
          return true;
        }
        if (cell == null) return false;
        if (EXACT_FIELDS.includes(f.id)) return cell === val;
        return String(cell).toLowerCase().includes(String(val).toLowerCase());
      });
    }
    return result;
  }, [users, ageRange, columnFilters]);

  // ─── Modal state ─────────────────────────────────────────────────────────────
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const hasFetchedRef = useRef(false);

  const selectedIds = useMemo(() => Object.keys(rowSelection).filter((id) => rowSelection[id]), [rowSelection]);
  const isAllSelected = selectedIds.length === users.length && users.length > 0;

  const isFormValid =
    notificationTitle.trim() !== "" &&
    notificationBody.trim() !== "" &&
    (!scheduleEnabled || (scheduleDate !== null && scheduleTime.trim() !== "")) &&
    selectedIds.length > 0;

  // ─── Load all users for recipient selection ───────────────────────────────────
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const res = await trpc.users.list.query({ limit: 1000 });
        setUsers(res.data.users as UserRow[]);
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // ─── Select all / deselect all ───────────────────────────────────────────────
  const handleSelectAll = () => {
    if (isAllSelected) {
      setRowSelection({});
    } else {
      const all: MRT_RowSelectionState = {};
      users.forEach((u) => {
        all[u.id] = true;
      });
      setRowSelection(all);
    }
  };

  // ─── Validation & submit ─────────────────────────────────────────────────────
  const handleCreateClick = () => {
    setConfirmOpened(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await trpc.appUserNotification.create.mutate({
        title: notificationTitle.trim(),
        body: notificationBody.trim(),
        sendToAll: isAllSelected,
        userIds: isAllSelected ? undefined : selectedIds,
        scheduledAt: scheduleEnabled && scheduleDate ? buildScheduledAt(scheduleDate, scheduleTime) : null,
      });
      successNotification({ title: "Notification Created", message: "Notification created successfully." });
      navigate("/notifications");
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSubmitting(false);
      setConfirmOpened(false);
    }
  };

  // ─── Recipient table columns ──────────────────────────────────────────────────
  const columns: MRT_ColumnDef<UserRow>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 160,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue<string>() || "—",
      },
      {
        accessorKey: "email",
        header: "Email ID",
        size: 200,
        enableSorting: true,
      },
      {
        accessorKey: "investmentAccountStatus",
        header: "Investment Acc Status",
        size: 190,
        filterVariant: "select",
        mantineFilterSelectProps: { data: UCC_STATUS_OPTIONS, clearable: true },
        enableSorting: false,
        Cell: ({ cell }) => <AdminStatusBadge status={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "age",
        header: "Age",
        size: 200,
        enableSorting: true,
        Filter: ({ column }) => {
          const value = (column.getFilterValue() as [number, number]) ?? [AGE_MIN, AGE_MAX];
          return (
            <RangeSlider
              min={AGE_MIN}
              max={AGE_MAX}
              value={value}
              onChangeEnd={(val) => {
                column.setFilterValue(val[0] === AGE_MIN && val[1] === AGE_MAX ? undefined : val);
                setAgeRange(val);
              }}
              minRange={1}
              size="sm"
              label={(v) => String(v)}
              mt={12}
              style={{ width: "80%" }}
            />
          );
        },
      },
      {
        accessorKey: "goalsCreated",
        header: "No of Goal Created",
        size: 150,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "dateOfBirth",
        header: "Date of Birth",
        size: 220,
        filterVariant: "date-range",
        enableSorting: false,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "gender",
        header: "Gender",
        size: 140,
        filterVariant: "select",
        mantineFilterSelectProps: { data: GENDER_OPTIONS, clearable: true },
        enableSorting: false,
        Cell: ({ cell }) => enumLabel(GENDER_LABEL, cell.getValue<string | null>()) ?? "—",
      },
      {
        accessorKey: "panNumber",
        header: "PAN",
        size: 160,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "investorType",
        header: "Investor Type",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "occupation",
        header: "Occupation",
        size: 200,
        filterVariant: "select",
        mantineFilterSelectProps: { data: OCCUPATION_OPTIONS, clearable: true },
        enableSorting: false,
        Cell: ({ cell }) => enumLabel(OCCUPATION_LABEL, cell.getValue<string | null>()) ?? "—",
      },
      {
        accessorKey: "incomeSlab",
        header: "Income Slab",
        size: 200,
        filterVariant: "select",
        mantineFilterSelectProps: { data: INCOME_SLAB_OPTIONS, clearable: true },
        enableSorting: false,
        Cell: ({ cell }) => enumLabel(INCOME_SLAB_LABEL, cell.getValue<string | null>()) ?? "—",
      },
      {
        accessorKey: "city",
        header: "City",
        size: 130,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "state",
        header: "State",
        size: 130,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "country",
        header: "Country",
        size: 100,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "placeOfBirth",
        header: "Place of Birth",
        size: 160,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "referralCode",
        header: "Referral Code",
        size: 150,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "referralCount",
        header: "Referral Count",
        size: 180,
        filterVariant: "range",
        enableSorting: true,
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 220,
        filterVariant: "date-range",
        enableSorting: true,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated At",
        size: 220,
        filterVariant: "date-range",
        enableSorting: true,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
    ],
    []
  );

  return (
    <Flex direction="column" w="100%" h="100vh" style={{ overflow: "hidden" }}>
      {/* ── Top bar ── */}
      <Flex
        align="center"
        justify="space-between"
        px="xl"
        py="md"
        style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
      >
        <Flex align="center" gap="sm">
          <ActionIcon variant="subtle" color="slate" onClick={() => navigate("/notifications")}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Title order={3} fw={600}>
            Create Notification
          </Title>
        </Flex>
        <Button color="primary" onClick={handleCreateClick} disabled={!isFormValid}>
          {scheduleEnabled ? "Create" : "Send Instantly"}
        </Button>
      </Flex>

      {/* ── Body ── */}
      <Flex flex={1} style={{ overflow: "hidden" }}>
        {/* ── Left: Add Content details ── */}
        <Flex
          direction="column"
          w={360}
          style={{
            minWidth: 320,
            maxWidth: 400,
            borderRight: "1px solid var(--mantine-color-gray-2)",
            overflowY: "auto",
          }}
          p="xl"
          gap="md"
        >
          <Text fw={700} fz="md">
            Add Content details
          </Text>

          {/* Schedule toggle card */}
          <Box
            p="md"
            style={{
              backgroundColor: "var(--mantine-color-primary-0)",
              borderRadius: "var(--mantine-radius-md)",
            }}
          >
            <Flex justify="space-between" align="center" mb="xs">
              <Text fw={600} fz="sm">
                Schedule Notification?
              </Text>
              <Switch
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.currentTarget.checked)}
                size="md"
              />
            </Flex>
            <Text fz="xs" c="slate.6" lh={1.5}>
              You can schedule notification according to desired date and time if not selected the notification will go
              directly at the same time
            </Text>
          </Box>

          {scheduleEnabled && (
            <>
              <DateInput
                label="Date"
                placeholder="Schedule date"
                required
                value={scheduleDate}
                onChange={setScheduleDate}
                minDate={new Date()}
                clearable
                rightSection={<IconCalendar size={16} color="var(--mantine-color-slate-4)" />}
              />

              <TimeInput
                label="Time"
                placeholder="Schedule time"
                required
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.currentTarget.value)}
                rightSection={<IconClock size={16} color="var(--mantine-color-slate-4)" />}
              />
            </>
          )}

          <TextInput
            label="Notification title"
            placeholder="Add title"
            required
            value={notificationTitle}
            onChange={(e) => setNotificationTitle(e.currentTarget.value)}
          />

          <Textarea
            label="Notification body"
            placeholder="Add Content"
            required
            autosize
            minRows={3}
            maxRows={5}
            value={notificationBody}
            onChange={(e) => setNotificationBody(e.currentTarget.value)}
          />
        </Flex>

        {/* ── Right: Select Recipient ── */}
        <Flex direction="column" flex={1} p="xl" gap="md" style={{ overflow: "hidden" }}>
          {/* Header row */}
          <Flex align="center" justify="space-between">
            <Flex align="center" gap="xl">
              <Text fw={600} fz="sm">
                Select Recipient
              </Text>
              <Flex gap="xs" align="center">
                <Text fz="sm" c="slate.5">
                  Total Users:
                </Text>
                <Text fz="sm" fw={600}>
                  {usersLoading ? "—" : users.length}
                </Text>
              </Flex>
              <Flex gap="xs" align="center">
                <Text fz="sm" c="slate.5">
                  Selected Users:
                </Text>
                <Text fz="sm" fw={600} c="primary">
                  {isAllSelected ? users.length : selectedIds.length}
                </Text>
              </Flex>
            </Flex>
            <Checkbox
              label="Select All"
              labelPosition="left"
              checked={isAllSelected}
              onChange={handleSelectAll}
              disabled={usersLoading}
              styles={{ label: { fontWeight: 600, fontSize: "var(--mantine-font-size-sm)" } }}
            />
          </Flex>

          {/* Table */}
          {usersLoading ? (
            <Center flex={1}>
              <Loader size="lg" type="dots" />
            </Center>
          ) : (
            <Box style={{ flex: 1, overflow: "hidden" }}>
              <MantineReactTable
                columns={columns}
                data={filteredUsers}
                getRowId={(row) => row.id}
                enableRowNumbers
                enableStickyHeader
                enableRowSelection
                enableSelectAll={false}
                onRowSelectionChange={setRowSelection}
                onColumnFiltersChange={setColumnFilters}
                state={{ rowSelection, columnFilters }}
                enableColumnPinning
                enableColumnResizing
                columnResizeMode="onEnd"
                layoutMode="semantic"
                manualFiltering
                enableGlobalFilter={false}
                positionToolbarAlertBanner="none"
                paginationDisplayMode="pages"
                mantinePaginationProps={{
                  rowsPerPageOptions: ["15", "25", "50"],
                }}
                initialState={{
                  columnPinning: { right: ["mrt-row-select"] },
                  density: "xs",
                  pagination: { pageIndex: 0, pageSize: 15 },
                }}
                mantineTableBodyCellProps={{
                  style: {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    borderRight: "1px solid var(--mantine-color-gray-2)",
                  },
                }}
                mantineTableHeadCellProps={{
                  style: {
                    fontWeight: 400,
                    fontSize: "var(--mantine-font-size-sm)",
                    color: theme.colors.slate[5],
                    borderRight: "1px solid var(--mantine-color-gray-2)",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  },
                }}
                mantineTableContainerProps={{
                  style: {
                    maxHeight: "calc(100vh - 310px)",
                    minHeight: "100px",
                    width: "100%",
                    overflowX: "auto",
                  },
                }}
                renderEmptyRowsFallback={() => (
                  <tr>
                    <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
                      No users found.
                    </td>
                  </tr>
                )}
              />
            </Box>
          )}
        </Flex>
      </Flex>

      {/* ── Confirmation modal ── */}
      <ConfirmNotificationModal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        onConfirm={handleConfirm}
        loading={submitting}
        mode="create"
        scheduleEnabled={scheduleEnabled}
      />
    </Flex>
  );
}
